import { Injectable, OnDestroy, WritableSignal, computed, inject, signal } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AUTH0_LIB_CONFIG } from '../types/auth0-config.token';

const ACCESS_TOKEN_KEY = 'access_token';
const SESSION_EXPIRED_KEY = 'session_expired';
const MAX_TIMEOUT_MS = 2_147_483_647;

type Timeout = ReturnType<typeof setTimeout>;

@Injectable({ providedIn: 'root' })
export class AuthenticationService implements OnDestroy {
    readonly #jwtHelper = new JwtHelperService();
    readonly #storageKey = inject(AUTH0_LIB_CONFIG).storageKey;

    readonly hasValidToken: WritableSignal<boolean> = signal<boolean>(false);
    readonly isAuthenticated = computed(() => this.hasValidToken());
    readonly isLoginEvent: WritableSignal<boolean> = signal<boolean>(false);

    readonly #isSessionExpired = signal(false);
    readonly isSessionExpired = this.#isSessionExpired.asReadonly();

    #expirationTimeout: Timeout | null = null;
    #hasInitialized = false;

    // --- Token storage ---

    /**
     * Checks if a valid token exists in storage.
     * If access_token (raw OAuth JWT) is present and valid, moves it to kempower-csms-auth
     * and sets isLoginEvent to true.
     */
    tokenIsVerified(): boolean {
        const authStorage = localStorage.getItem(this.#storageKey);
        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

        if (!authStorage && !accessToken) {
            return false;
        }

        if (accessToken) {
            if (this.#jwtHelper.isTokenExpired(accessToken)) {
                this.hasValidToken.set(false);
                return false;
            }
            this.setTokenInStorage(accessToken);
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            this.isLoginEvent.set(true);
        }

        if (authStorage) {
            try {
                const auth = JSON.parse(authStorage);
                if (this.#jwtHelper.isTokenExpired(auth.jwt)) {
                    this.hasValidToken.set(false);
                    return false;
                }
            } catch {
                this.hasValidToken.set(false);
                return false;
            }
        }

        this.hasValidToken.set(true);
        return true;
    }

    /** Stores the JWT in kempower-csms-auth as { jwt: token }. */
    setTokenInStorage(token: string): void {
        localStorage.setItem(this.#storageKey, JSON.stringify({ jwt: token }));
    }

    /** Returns true if the stored JWT is expired. */
    isJwtExpired(): boolean {
        const jwt = this.getJwt();
        if (!jwt) return true;
        return this.#jwtHelper.isTokenExpired(jwt);
    }

    /**
     * Returns true if the stored JWT is structurally invalid:
     * cannot be decoded, missing an exp claim, or has no payload.
     * Note: this does NOT verify the signature — only the server can do that.
     * A token missing exp is treated as invalid because our auth provider always sets it.
     */
    isJwtInvalid(): boolean {
        const jwt = this.getJwt();
        if (!jwt) return true;
        try {
            const decoded = this.#jwtHelper.decodeToken(jwt);
            if (!decoded) return true;
            return this.#jwtHelper.getTokenExpirationDate(jwt) === null;
        } catch {
            return true;
        }
    }

    /** Returns the JWT from kempower-csms-auth, or null if absent or malformed. */
    getJwt(): string | null {
        const authData = localStorage.getItem(this.#storageKey);
        if (!authData) return null;
        try {
            return JSON.parse(authData)?.jwt ?? null;
        } catch {
            return null;
        }
    }

    // --- Session expiration ---

    /** Schedules a timeout to set isSessionExpired when the JWT's exp claim is reached. */
    initTokenExpirationWatch(): void {
        if (this.#hasInitialized) return;
        this.#hasInitialized = true;
        this.#scheduleExpirationNotification();
    }

    /**
     * Call after a new login to check if a previously-expired session has been restored.
     * Returns true if the session was restored (caller should show UI feedback).
     */
    checkTokenRestored(): boolean {
        const wasExpired = this.isSessionExpired() || sessionStorage.getItem(SESSION_EXPIRED_KEY) === 'true';
        if (!wasExpired) return false;

        const jwt = this.getJwt();
        if (!jwt || this.#jwtHelper.isTokenExpired(jwt)) return false;

        sessionStorage.removeItem(SESSION_EXPIRED_KEY);
        this.#handleRestored();
        return true;
    }

    /**
     * Resets all auth state. Call before logout to prevent trailing 401s
     * from triggering the session-expired snackbar.
     */
    clearSession(): void {
        this.hasValidToken.set(false);
        this.#isSessionExpired.set(false);
        this.#clearScheduledTimeout();
        this.#hasInitialized = false;
        sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    }

    /**
     * Call when a request fails due to an invalid or expired token.
     * Sets isSessionExpired if the JWT has expired.
     * Distinct from a 401 caused by missing permissions — that is an
     * authorization failure, not a session/token problem.
     */
    handleInvalid(): void {
        if (!this.#hasInitialized) return;
        if (this.isSessionExpired()) return;
        const jwt = this.getJwt();
        if (!jwt) return;
        if (this.#jwtHelper.isTokenExpired(jwt)) {
            this.#handleExpired();
        }
    }

    ngOnDestroy(): void {
        this.#clearScheduledTimeout();
    }

    #scheduleExpirationNotification(): void {
        this.#clearScheduledTimeout();

        const jwt = this.getJwt();
        if (!jwt) return;

        const expirationDate = this.#jwtHelper.getTokenExpirationDate(jwt);
        if (!expirationDate) return;

        const msUntilExpiry = expirationDate.getTime() - Date.now();

        if (msUntilExpiry <= 0) {
            this.#handleExpired();
            return;
        }

        const delay = Math.min(msUntilExpiry, MAX_TIMEOUT_MS);
        this.#expirationTimeout = setTimeout(
            () => (delay < msUntilExpiry ? this.#scheduleExpirationNotification() : this.#handleExpired()),
            delay,
        );
    }

    #clearScheduledTimeout(): void {
        if (this.#expirationTimeout !== null) {
            clearTimeout(this.#expirationTimeout);
            this.#expirationTimeout = null;
        }
    }

    #handleExpired(): void {
        this.#isSessionExpired.set(true);
        sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true');
    }

    #handleRestored(): void {
        this.#isSessionExpired.set(false);
        this.#scheduleExpirationNotification();
    }
}
